import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  GlobeAltIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  Squares2X2Icon,
  CheckBadgeIcon,
  ArrowUturnLeftIcon,
  EyeIcon,
  SwatchIcon,
  TrashIcon,
  BookmarkSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  Input,
  Select,
  LoadingSpinner,
  Badge,
  Modal,
  ModalFooter,
} from '@/components/common';
import { useAuthStore } from '@/store';
import { cn, formatDateTime } from '@/utils';
import api from '@/services/api';
import {
  MICROSITE_PAGE_TYPES,
  type MicrositeDevice,
  type MicrositePageType,
  type MicrositeSection,
  type MicrositeSectionTemplate,
  type SectionTypeDef,
  type MicrositeCatalogInsurer,
} from '@/services/microsite';
import {
  useSectionTypes,
  useMicrositeLayout,
  useUpdateMicrositeLayout,
  useCustomizeMicrositeLayout,
  useResetMicrositeToBase,
  useSubmitMicrositeDomain,
  useApproveMicrositeDomain,
  useMicrositePublicSlug,
  usePublicSiteStatus,
  useUpdatePublicSiteStatus,
  useMicrositeCatalog,
  useSetItemVisibility,
  useMakeAllPublic,
  useMicrositePreview,
  useSectionTemplates,
  useSaveSectionTemplate,
  useDeleteSectionTemplate,
} from '@/hooks/useMicrosite';
import { SectionCard } from './microsite/SectionCard';
import { PreviewPane } from './microsite/PreviewPane';
import { ThemePanel } from './microsite/ThemePanel';
import {
  duplicateSection as makeDuplicate,
  newSectionId,
  renumber,
  toggleDeviceVisibility,
  useDebouncedValue,
} from './microsite/sectionUtils';

export type MicrositeMode = 'broker' | 'base';

type BuilderTab = 'builder' | 'theme' | 'domain' | 'products';

// =============================================================================
// Page
// =============================================================================
export default function MicrositeBuilderPage({ mode = 'broker' }: { mode?: MicrositeMode }) {
  const [tab, setTab] = useState<BuilderTab>('builder');

  const isBase = mode === 'base';
  const builderLabel = isBase ? 'Base Templates' : 'Page Builder';
  const secondTabLabel = isBase ? 'Domain Approvals' : 'Custom Domain';

  return (
    <div className="space-y-6">
      <PageHeader
        title={isBase ? 'Microsite Base Templates' : 'Public Site Builder'}
        subtitle={
          isBase
            ? 'Edit the shared BASE templates every broker inherits until they customize.'
            : 'Design your public-facing microsite pages and manage your custom domain.'
        }
      />

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex flex-wrap gap-6">
          <TabButton active={tab === 'builder'} onClick={() => setTab('builder')} icon={Squares2X2Icon}>
            {builderLabel}
          </TabButton>
          <TabButton active={tab === 'theme'} onClick={() => setTab('theme')} icon={SwatchIcon}>
            Theme
          </TabButton>
          <TabButton active={tab === 'domain'} onClick={() => setTab('domain')} icon={GlobeAltIcon}>
            {secondTabLabel}
          </TabButton>
          {!isBase && (
            <TabButton active={tab === 'products'} onClick={() => setTab('products')} icon={EyeIcon}>
              Products &amp; Pages
            </TabButton>
          )}
        </nav>
      </div>

      {tab === 'builder' && <BuilderPanel mode={mode} />}
      {tab === 'theme' && <ThemePanel />}
      {tab === 'domain' && (isBase ? <DomainApprovalPanel /> : <DomainPanel />)}
      {tab === 'products' && !isBase && <ProductsVisibilityPanel />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors',
        active
          ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
      )}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );
}

// =============================================================================
// Publish card — broker self-service: turn the public site on/off
// =============================================================================
function PublishCard() {
  const { data: status, isLoading } = usePublicSiteStatus();
  const update = useUpdatePublicSiteStatus();
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (status && !dirty) {
      setSlug(status.public_profile_slug ?? '');
      setDescription(status.description ?? '');
    }
  }, [status, dirty]);

  if (isLoading || !status) {
    return (
      <Card className="p-5">
        <LoadingSpinner />
      </Card>
    );
  }

  const enabled = status.is_public_profile_enabled;
  const live = status.is_live;
  const awaitingApproval = enabled && !live;

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">Public website</h3>
            <Badge variant={live ? 'success' : awaitingApproval ? 'warning' : 'default'}>
              {live ? 'Live' : awaitingApproval ? 'Not live yet' : 'Off'}
            </Badge>
          </div>
          <p className="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
            {awaitingApproval
              ? `Your public website is switched on, but it is not visible yet because your broker account is "${status.broker_status}". It goes live once your account is active.`
              : live
              ? 'Your public site is live and shareable. Anyone with the link can view your insurers and products and send you an enquiry.'
              : `Turn on a public, shareable website listing the ${status.product_count} products you offer, with an enquiry form that drops leads straight into your contacts.`}
          </p>
        </div>

        {/* on/off toggle */}
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={update.isPending}
          onClick={() => update.mutate({ is_public_profile_enabled: !enabled })}
          className={cn(
            'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
            enabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600',
          )}
        >
          <span
            className={cn(
              'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
              enabled ? 'translate-x-5' : 'translate-x-0.5',
            )}
          />
        </button>
      </div>

      {live && status.public_url && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
          <a
            href={status.public_url}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            {status.public_url}
          </a>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              navigator.clipboard?.writeText(status.public_url!);
              toast.success('Link copied');
            }}
          >
            Copy link
          </Button>
          <a href={status.public_url} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="secondary">Open ↗</Button>
          </a>
        </div>
      )}

      {/* slug + description (address + tagline) */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Web address (slug)
          </label>
          <Input
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setDirty(true);
            }}
            placeholder="my-brokerage"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Short description
          </label>
          <Input
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setDirty(true);
            }}
            placeholder="Compare and buy insurance with us."
          />
        </div>
      </div>
      {dirty && (
        <div className="mt-3 flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setSlug(status.public_profile_slug ?? '');
              setDescription(status.description ?? '');
              setDirty(false);
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                { public_profile_slug: slug.trim() || undefined, description },
                { onSuccess: () => setDirty(false) },
              )
            }
          >
            Save
          </Button>
        </div>
      )}
    </Card>
  );
}

// =============================================================================
// Builder — page-type switcher + per-page editor
// =============================================================================
function BuilderPanel({ mode }: { mode: MicrositeMode }) {
  const [pageType, setPageType] = useState<MicrositePageType>('landing');
  const { data: sectionTypes, isLoading: typesLoading } = useSectionTypes();

  return (
    <div className="space-y-6 pb-24">
      {mode === 'broker' && <PublishCard />}

      <div className="flex flex-wrap items-center gap-2">
        {MICROSITE_PAGE_TYPES.map((pt) => (
          <button
            key={pt.value}
            onClick={() => setPageType(pt.value)}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              pageType === pt.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600',
            )}
          >
            {pt.label}
          </button>
        ))}
      </div>

      {typesLoading || !sectionTypes ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <BuilderBody key={pageType} mode={mode} pageType={pageType} sectionTypes={sectionTypes} />
      )}
    </div>
  );
}

function BuilderBody({
  mode,
  pageType,
  sectionTypes,
}: {
  mode: MicrositeMode;
  pageType: MicrositePageType;
  sectionTypes: Record<string, SectionTypeDef>;
}) {
  const { data: layout, isLoading } = useMicrositeLayout(pageType);
  const update = useUpdateMicrositeLayout();
  const customize = useCustomizeMicrositeLayout();
  const reset = useResetMicrositeToBase();
  const { data: templates } = useSectionTemplates();
  const saveTemplate = useSaveSectionTemplate();

  const [sections, setSections] = useState<MicrositeSection[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [addType, setAddType] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [device, setDevice] = useState<MicrositeDevice>('desktop');
  const [showPreview, setShowPreview] = useState(true);
  const [templateTarget, setTemplateTarget] = useState<MicrositeSection | null>(null);

  // Broker owns its own row only when a row exists that is NOT the BASE row.
  const hasOwnRow = mode === 'broker' && layout?.exists === true && layout?.is_base === false;
  // In broker mode, "inherited" = still showing the shared BASE (or nothing seeded).
  const inherited = mode === 'broker' && !hasOwnRow;
  const editable = mode === 'base' || hasOwnRow;

  useEffect(() => {
    if (layout) {
      const ordered = [...(layout.sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSections(ordered);
      setIsDirty(false);
    }
  }, [layout]);

  // The preview is a server render; debounce so typing costs one render, not one
  // per keystroke.
  const debouncedSections = useDebouncedValue(sections, 500);
  const preview = useMicrositePreview({
    pageType,
    sections: debouncedSections,
    device,
    enabled: showPreview,
  });

  // dnd-kit: a pointer must travel a few pixels before a drag starts, otherwise
  // every click on a button inside the card header would be swallowed as a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const mutate = (fn: (prev: MicrositeSection[]) => MicrositeSection[]) => {
    setSections(fn);
    setIsDirty(true);
  };

  const updateSection = (index: number, next: MicrositeSection) =>
    mutate((prev) => prev.map((s, i) => (i === index ? next : s)));

  const moveSection = (index: number, dir: -1 | 1) =>
    mutate((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      return arrayMove(prev, index, target);
    });

  const removeSection = (index: number) => mutate((prev) => prev.filter((_, i) => i !== index));

  const duplicate = (index: number) =>
    mutate((prev) => {
      const copy = [...prev];
      // A duplicate gets a brand-new id (ids must be unique on a page) and is
      // dropped directly below its original, where the author is looking.
      copy.splice(index + 1, 0, makeDuplicate(prev[index]));
      return copy;
    });

  const toggleHidden = (index: number) =>
    mutate((prev) => prev.map((s, i) => (i === index ? { ...s, hidden: !s.hidden } : s)));

  const toggleDevice = (index: number, target: MicrositeDevice) =>
    mutate((prev) => prev.map((s, i) => (i === index ? toggleDeviceVisibility(s, target) : s)));

  const addSection = () => {
    if (!addType) return;
    mutate((prev) => [...prev, { id: newSectionId(), type: addType, order: prev.length, config: {} }]);
    setAddType('');
  };

  const insertTemplate = () => {
    const template = (templates ?? []).find((t) => t.id === templateId);
    if (!template) return;
    mutate((prev) => [
      ...prev,
      {
        id: newSectionId(),
        type: template.section_type,
        order: prev.length,
        config: JSON.parse(JSON.stringify(template.config ?? {})),
      },
    ]);
    setTemplateId('');
    toast.success(`Inserted "${template.name}"`);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    mutate((prev) => {
      const from = prev.findIndex((s) => s.id === active.id);
      const to = prev.findIndex((s) => s.id === over.id);
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to);
    });
  };

  const handleSave = () => {
    update.mutate(
      { pageType, sections: renumber(sections), isPublished: layout?.is_published ?? true },
      { onSuccess: () => setIsDirty(false) },
    );
  };

  const handleDiscard = () => {
    const ordered = [...(layout?.sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setSections(ordered);
    setIsDirty(false);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const paletteOptions = Object.entries(sectionTypes).map(([type, def]) => ({
    value: type,
    label: def.label,
  }));

  const sectionIds = sections.map((s) => s.id);

  return (
    <div className="space-y-4">
      {mode === 'broker' && (
        <ScopeBanner inherited={inherited} pageType={pageType} customize={customize} reset={reset} />
      )}
      {mode === 'base' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
          You are editing the shared <strong>BASE</strong> template. Changes affect every broker
          that has not customized this page.
        </div>
      )}

      <div className="flex items-center justify-end">
        <label className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <input
            type="checkbox"
            checked={showPreview}
            onChange={(e) => setShowPreview(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Show live preview
        </label>
      </div>

      <div
        className={cn(
          'grid gap-6',
          showPreview && 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,520px)_minmax(0,1fr)]',
        )}
      >
        <div className="space-y-4">
          {sections.length === 0 && (
            <Card className="p-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No sections yet.{' '}
                {editable ? 'Add one below to get started.' : 'Customize this page to start editing.'}
              </p>
            </Card>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {sections.map((section, index) => (
                  <SectionCard
                    key={section.id}
                    def={sectionTypes[section.type]}
                    section={section}
                    index={index}
                    total={sections.length}
                    editable={editable}
                    onChange={(next) => updateSection(index, next)}
                    onMoveUp={() => moveSection(index, -1)}
                    onMoveDown={() => moveSection(index, 1)}
                    onDuplicate={() => duplicate(index)}
                    onToggleHidden={() => toggleHidden(index)}
                    onToggleDevice={(target) => toggleDevice(index, target)}
                    onSaveAsTemplate={() => setTemplateTarget(section)}
                    onRemove={() => removeSection(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {editable && (
            <Card className="space-y-4 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <Select
                    label="Add a section"
                    name="add-section-type"
                    value={addType}
                    onChange={(e) => setAddType(e.target.value)}
                    placeholder="Choose a section type…"
                    options={paletteOptions}
                  />
                </div>
                <Button
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                  onClick={addSection}
                  disabled={!addType}
                >
                  Add Section
                </Button>
              </div>

              {(templates?.length ?? 0) > 0 && (
                <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-end dark:border-gray-700">
                  <div className="flex-1">
                    <Select
                      label="Or reuse a saved section"
                      name="reuse-section-template"
                      value={templateId}
                      onChange={(e) => setTemplateId(e.target.value)}
                      placeholder="Choose a saved template…"
                      options={(templates ?? []).map((t) => ({
                        value: t.id,
                        label: t.is_shared ? `${t.name} (platform)` : t.name,
                      }))}
                    />
                  </div>
                  <Button
                    variant="secondary"
                    leftIcon={<BookmarkSquareIcon className="h-4 w-4" />}
                    onClick={insertTemplate}
                    disabled={!templateId}
                  >
                    Insert
                  </Button>
                </div>
              )}
            </Card>
          )}

          {editable && <SectionTemplateLibrary templates={templates ?? []} />}
        </div>

        {showPreview && (
          <PreviewPane
            html={preview.data?.html}
            device={device}
            onDeviceChange={setDevice}
            isLoading={preview.isLoading}
            isFetching={preview.isFetching}
            error={
              preview.error
                ? 'This draft could not be rendered. Check the highlighted fields — a bad link or value will also block saving.'
                : null
            }
            onRetry={() => preview.refetch()}
          />
        )}
      </div>

      <SaveTemplateModal
        section={templateTarget}
        sectionLabel={templateTarget ? (sectionTypes[templateTarget.type]?.label ?? templateTarget.type) : ''}
        onClose={() => setTemplateTarget(null)}
        onSave={(name) => {
          if (!templateTarget) return;
          saveTemplate.mutate(
            {
              name,
              section_type: templateTarget.type,
              // Only the type + config travel: a template must never carry a
              // section id, or inserting it twice would collide.
              config: JSON.parse(JSON.stringify(templateTarget.config ?? {})),
            },
            { onSuccess: () => setTemplateTarget(null) },
          );
        }}
        isSaving={saveTemplate.isPending}
      />

      {isDirty && editable && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-end gap-3 border-t border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <span className="text-sm text-gray-500 dark:text-gray-400">You have unsaved changes</span>
          <Button variant="secondary" onClick={handleDiscard}>
            Discard
          </Button>
          <Button onClick={handleSave} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save Page'}
          </Button>
        </div>
      )}
    </div>
  );
}

function SaveTemplateModal({
  section,
  sectionLabel,
  onClose,
  onSave,
  isSaving,
}: {
  section: MicrositeSection | null;
  sectionLabel: string;
  onClose: () => void;
  onSave: (name: string) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState('');

  useEffect(() => {
    if (section) setName(sectionLabel);
  }, [section, sectionLabel]);

  return (
    <Modal isOpen={!!section} onClose={onClose} title="Save section as a template">
      <div className="space-y-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Saves this section&apos;s settings so you can drop it onto another page. Later edits to
          the page section do not change the template.
        </p>
        <Input
          id="section-template-name"
          label="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Standard FAQ block"
          autoFocus
        />
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!name.trim() || isSaving} onClick={() => onSave(name.trim())}>
          {isSaving ? 'Saving…' : 'Save template'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function SectionTemplateLibrary({ templates }: { templates: MicrositeSectionTemplate[] }) {
  const remove = useDeleteSectionTemplate();
  const own = templates.filter((t) => !t.is_shared);
  if (own.length === 0) return null;

  return (
    <Card className="p-4">
      <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
        Your saved sections
      </h4>
      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        {own.map((template) => (
          <li key={template.id} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm text-gray-700 dark:text-gray-300">{template.name}</p>
              <p className="text-xs text-gray-400">{template.section_type}</p>
            </div>
            <button
              type="button"
              title={`Delete ${template.name}`}
              aria-label={`Delete ${template.name}`}
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete the saved section "${template.name}"?`)) {
                  remove.mutate(template.id);
                }
              }}
              className="rounded-md p-1.5 text-red-500 transition-colors hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-900/20"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ScopeBanner({
  inherited,
  pageType,
  customize,
  reset,
}: {
  inherited: boolean;
  pageType: MicrositePageType;
  customize: ReturnType<typeof useCustomizeMicrositeLayout>;
  reset: ReturnType<typeof useResetMicrositeToBase>;
}) {
  const { user } = useAuthStore();
  const { data: slug } = useMicrositePublicSlug(user?.tenant_id ?? undefined);
  const previewHref = slug ? `/policy/site/${slug}/` : null;

  if (inherited) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm dark:border-blue-900/50 dark:bg-blue-900/20 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-blue-800 dark:text-blue-300">
          This page currently inherits the platform <strong>BASE</strong> template. Customize it to
          make your own edits.
        </div>
        <div className="flex items-center gap-2">
          <PreviewLink href={previewHref} />
          <Button
            size="sm"
            leftIcon={<CheckBadgeIcon className="h-4 w-4" />}
            onClick={() => customize.mutate(pageType)}
            disabled={customize.isPending}
          >
            {customize.isPending ? 'Customizing…' : 'Customize from base'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm dark:border-green-900/50 dark:bg-green-900/20 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-green-800 dark:text-green-300">
        <Badge variant="success" size="sm">
          Customized
        </Badge>
        <span>This is your own copy of this page.</span>
      </div>
      <div className="flex items-center gap-2">
        <PreviewLink href={previewHref} />
        <Button
          size="sm"
          variant="outline"
          leftIcon={<ArrowUturnLeftIcon className="h-4 w-4" />}
          onClick={() => {
            if (
              window.confirm(
                'Reset to the base template? Your customizations for this page will be discarded.',
              )
            ) {
              reset.mutate(pageType);
            }
          }}
          disabled={reset.isPending}
        >
          {reset.isPending ? 'Resetting…' : 'Reset to base'}
        </Button>
      </div>
    </div>
  );
}

function PreviewLink({ href }: { href: string | null }) {
  if (!href) {
    return (
      <span
        className="text-xs text-gray-400 dark:text-gray-500"
        title="Enable your public profile to open the live site."
      >
        Live site unavailable
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
    >
      Open live site
      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
    </a>
  );
}

// =============================================================================
// Broker: custom-domain submit panel
// =============================================================================
function DomainPanel() {
  const submit = useSubmitMicrositeDomain();
  const [domain, setDomain] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) return;
    submit.mutate(domain.trim());
  };

  const result = submit.data;

  return (
    <div className="max-w-2xl space-y-6">
      <Card className="space-y-4 p-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Custom Domain</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Point your own domain (e.g. <code>buy.yourcompany.com</code>) at your public microsite.
            After submitting, add the DNS records below and ask a country admin to approve it.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Input
              label="Domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="buy.yourcompany.com"
            />
          </div>
          <Button type="submit" isLoading={submit.isPending} leftIcon={<GlobeAltIcon className="h-4 w-4" />}>
            Submit for verification
          </Button>
        </form>
      </Card>

      {result && (
        <Card className="space-y-3 p-6">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{result.domain}</h3>
            <Badge variant="warning" size="sm">
              {result.status}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">{result.instructions}</p>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="mb-1 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
              Verification token (DNS TXT: policy-verify=…)
            </p>
            <code className="break-all text-sm text-gray-900 dark:text-gray-100">
              {result.verification_token}
            </code>
          </div>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Country admin: domain approval panel (the pending queue + a manual fallback)
// =============================================================================

/**
 * One row of GET /microsite/domains/pending/ (apps.microsite.PendingDomainSerializer).
 *
 * `status` is normally 'PENDING' or 'FAILED' — a DNS-approval request still
 * waiting on the admin. 'VERIFIED' can also appear here: while TLS
 * provisioning is live on the host, a domain that passed DNS verification
 * but whose nginx vhost never got enabled stays on the queue instead of
 * silently vanishing (see apps.microsite.views.PendingDomainListView).
 */
interface PendingDomain {
  id: string;
  domain: string;
  broker: string;
  broker_name: string;
  broker_code: string;
  broker_country: string;
  status: 'PENDING' | 'FAILED' | 'VERIFIED';
  verification_token: string;
  created_at: string;
  verified_at: string | null;
}

const PENDING_DOMAINS_KEY = 'micrositePendingDomains';

/**
 * The approval queue. Read inline rather than through `services/microsite.ts`
 * because this is the only screen that consumes it; the endpoint is already
 * country-scoped server-side, so there is nothing to filter here.
 */
function usePendingDomains() {
  return useQuery({
    queryKey: [PENDING_DOMAINS_KEY],
    queryFn: async () => {
      const res = await api.get<PendingDomain[]>('/microsite/domains/pending/');
      return res.data;
    },
    staleTime: 1000 * 30,
  });
}

function DomainApprovalPanel() {
  const queryClient = useQueryClient();
  const approve = useApproveMicrositeDomain();
  const { data: pending, isLoading, isError } = usePendingDomains();
  const [domain, setDomain] = useState('');
  const [showManual, setShowManual] = useState(false);

  /** Approving removes the row from the queue, so refetch it either way. */
  const runApprove = (value: string) =>
    approve.mutate(value, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [PENDING_DOMAINS_KEY] }),
    });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) return;
    runApprove(domain.trim());
  };

  const result = approve.data;

  return (
    <div className="max-w-3xl space-y-6">
      <Card className="space-y-4 p-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Pending Domain Requests
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Custom domains brokers in your country have submitted and are waiting on. Check the
            DNS TXT record <code className="text-xs">policy-verify=&lt;token&gt;</code> resolves on
            the domain before approving — approval binds it to that broker.
          </p>
        </div>

        {isLoading && <LoadingSpinner />}

        {isError && (
          <p className="text-sm text-red-600 dark:text-red-400">
            Could not load the approval queue. Use manual entry below.
          </p>
        )}

        {!isLoading && !isError && pending?.length === 0 && (
          <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
            No domains are waiting for approval.
          </p>
        )}

        {!isLoading &&
          pending?.map((row) => (
            <div
              key={row.id}
              data-testid="pending-domain-row"
              className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-gray-900 dark:text-white">
                    {row.domain}
                  </span>
                  <Badge
                    variant={
                      row.status === 'FAILED'
                        ? 'error'
                        : row.status === 'VERIFIED'
                          ? 'info'
                          : 'warning'
                    }
                    size="sm"
                  >
                    {row.status === 'VERIFIED' ? 'DNS verified — TLS pending' : row.status}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {row.broker_name}
                  {row.broker_country ? ` · ${row.broker_country}` : ''}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Submitted {formatDateTime(row.created_at)}
                </p>
                <code className="block break-all text-xs text-gray-500 dark:text-gray-400">
                  policy-verify={row.verification_token}
                </code>
              </div>
              <Button
                size="sm"
                isLoading={approve.isPending && approve.variables === row.domain}
                onClick={() => runApprove(row.domain)}
                leftIcon={<CheckBadgeIcon className="h-4 w-4" />}
              >
                {row.status === 'VERIFIED' ? 'Retry TLS' : 'Approve'}
              </Button>
            </div>
          ))}
      </Card>

      <Card className="space-y-4 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Approve by domain
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Fallback for a domain that is not on the list above — approval is keyed by the exact
              domain string.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowManual((v) => !v)}>
            {showManual ? 'Hide' : 'Enter manually'}
          </Button>
        </div>
        {showManual && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Input
                id="approve-domain"
                label="Domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="buy.somebroker.com"
              />
            </div>
            <Button
              type="submit"
              isLoading={approve.isPending}
              leftIcon={<CheckBadgeIcon className="h-4 w-4" />}
            >
              Approve
            </Button>
          </form>
        )}
      </Card>

      {result && (
        <Card className="space-y-2 p-6">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{result.domain}</h3>
            <Badge variant="success" size="sm">
              {result.status}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Bound to <strong>{result.broker}</strong>.
          </p>
          <div className="flex items-center gap-2">
            <Badge variant={result.provisioning.applied ? 'success' : 'info'} size="sm">
              {result.provisioning.applied ? 'TLS live' : 'TLS pending'}
            </Badge>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {result.provisioning.detail}
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Products & Pages — per-product / per-insurer public show/hide control
// =============================================================================

/** Green pill on/off switch, copied from PublishCard for visual consistency. */
function VisibilityToggle({
  visible,
  disabled,
  onToggle,
  label,
}: {
  visible: boolean;
  disabled?: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={visible}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={cn(
        'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
        visible ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          visible ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

function OpenLink({ href }: { href: string | null }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
    >
      Open
      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
    </a>
  );
}

function ProductsVisibilityPanel() {
  const { user } = useAuthStore();
  const { data: catalog, isLoading } = useMicrositeCatalog();
  const { data: slug } = useMicrositePublicSlug(user?.tenant_id ?? undefined);
  const setVisibility = useSetItemVisibility();
  const makeAllPublic = useMakeAllPublic();

  const siteBase = slug ? `/policy/site/${slug}` : null;

  if (isLoading || !catalog) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const { insurers, insurer_count, hidden_count, all_public } = catalog;

  return (
    <div className="space-y-6 pb-12">
      {/* Master control */}
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold">Public pages</h3>
              <Badge variant={all_public ? 'success' : 'warning'}>
                {all_public ? 'All public' : `${hidden_count} hidden`}
              </Badge>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              Every company and product page is public by default. Hiding one only removes its page
              from your public site — it never affects what you can sell. A hidden product sends
              visitors to its company page; a hidden company sends them to your home page.
            </p>
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              {insurer_count} {insurer_count === 1 ? 'company' : 'companies'} · {hidden_count} hidden
            </p>
          </div>

          <Button
            size="sm"
            variant={all_public ? 'outline' : 'primary'}
            leftIcon={<CheckBadgeIcon className="h-4 w-4" />}
            disabled={all_public || makeAllPublic.isPending}
            onClick={() => makeAllPublic.mutate()}
          >
            {all_public
              ? 'Everything is public'
              : makeAllPublic.isPending
                ? 'Applying…'
                : 'Make all public'}
          </Button>
        </div>
      </Card>

      {insurers.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You have no products to show yet.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {insurers.map((insurer) => (
            <InsurerVisibilityCard
              key={insurer.id}
              insurer={insurer}
              siteBase={siteBase}
              busy={setVisibility.isPending}
              onToggleInsurer={(visible) =>
                setVisibility.mutate({ itemType: 'insurer', itemId: insurer.id, visible })
              }
              onToggleProduct={(productId, visible) =>
                setVisibility.mutate({ itemType: 'product', itemId: productId, visible })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InsurerVisibilityCard({
  insurer,
  siteBase,
  busy,
  onToggleInsurer,
  onToggleProduct,
}: {
  insurer: MicrositeCatalogInsurer;
  siteBase: string | null;
  busy: boolean;
  onToggleInsurer: (visible: boolean) => void;
  onToggleProduct: (productId: string, visible: boolean) => void;
}) {
  const insurerHref = siteBase ? `${siteBase}/company/${insurer.slug}/` : null;
  const hidden = !insurer.visible;

  return (
    <Card className="overflow-hidden p-0">
      {/* Insurer row */}
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/60">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
              {insurer.name}
            </span>
            {hidden ? (
              <Badge variant="default" size="sm">
                Hidden
              </Badge>
            ) : (
              <Badge variant="success" size="sm">
                Public
              </Badge>
            )}
          </div>
          {hidden && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              This company page and all its products are hidden from your public site.
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          <OpenLink href={insurerHref} />
          <VisibilityToggle
            visible={insurer.visible}
            disabled={busy}
            onToggle={() => onToggleInsurer(hidden)}
            label={`Toggle public visibility for ${insurer.name}`}
          />
        </div>
      </div>

      {/* Products */}
      <div className={cn('divide-y divide-gray-100 dark:divide-gray-700', hidden && 'opacity-50')}>
        {insurer.products.length === 0 ? (
          <p className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">
            No products under this company yet.
          </p>
        ) : (
          insurer.products.map((product) => {
            const productHref = siteBase
              ? `${siteBase}/company/${insurer.slug}/${product.slug}/`
              : null;
            const productHidden = !product.visible;
            return (
              <div key={product.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm text-gray-700 dark:text-gray-300">
                    {product.name}
                  </span>
                  {productHidden && !hidden && (
                    <Badge variant="default" size="sm">
                      Hidden
                    </Badge>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-3">
                  <OpenLink href={productHref} />
                  <VisibilityToggle
                    visible={product.visible}
                    disabled={busy || hidden}
                    onToggle={() => onToggleProduct(product.id, productHidden)}
                    label={`Toggle public visibility for ${product.name}`}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
